import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_FILTER, type FlightFilter } from '@flightscanner/shared';

import {
  DEFAULT_SATELLITE_FILTER,
  DEFAULT_VESSEL_FILTER,
  type SatelliteFilter,
  type VesselFilter,
} from '../data/types';

export type AppMode = 'map' | 'satellite';
export type ArMode = 'off' | 'sky-hud' | 'quicklook';
export type FilterTab = 'aircraft' | 'vessels' | 'satellites';

interface AppState {
  filter: FlightFilter;
  vesselFilter: VesselFilter;
  satelliteFilter: SatelliteFilter;
  showMilitaryGlow: boolean;
  preferLabelCallsign: boolean;

  appMode: AppMode;
  filterTab: FilterTab;
  selectedIcao: string | null;
  selectedVesselId: string | null;
  selectedSatId: string | null;
  arMode: ArMode;
  filtersOpen: boolean;
  panelOpen: boolean;
  hudPermissionAsked: boolean;
  followSat: boolean;
  focusIcao: string | null;
  focusPoint: { lon: number; lat: number } | null;
  focusSeq: number;
  focusVesselId: string | null;
  focusVesselPoint: { lon: number; lat: number } | null;
  focusVesselSeq: number;
  focusSatId: string | null;
  focusSatSeq: number;

  setFilter: (patch: Partial<FlightFilter>) => void;
  resetFilter: () => void;
  setVesselFilter: (patch: Partial<VesselFilter>) => void;
  resetVesselFilter: () => void;
  setSatelliteFilter: (patch: Partial<SatelliteFilter>) => void;
  resetSatelliteFilter: () => void;
  setAppMode: (mode: AppMode) => void;
  setFilterTab: (tab: FilterTab) => void;
  setSelected: (icao: string | null) => void;
  setSelectedVessel: (id: string | null) => void;
  setSelectedSat: (noradId: string | null) => void;
  focusAircraft: (icao: string, point?: { lon: number; lat: number } | null) => void;
  focusVessel: (id: string, point?: { lon: number; lat: number } | null) => void;
  focusSatellite: (noradId: string) => void;
  clearFocus: () => void;
  setArMode: (m: ArMode) => void;
  setFiltersOpen: (v: boolean) => void;
  setPanelOpen: (v: boolean) => void;
  setFollowSat: (v: boolean) => void;
  setHudPermissionAsked: (v: boolean) => void;
  togglePref: (k: 'showMilitaryGlow' | 'preferLabelCallsign', v: boolean) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      filter: { ...DEFAULT_FILTER },
      vesselFilter: { ...DEFAULT_VESSEL_FILTER },
      satelliteFilter: { ...DEFAULT_SATELLITE_FILTER },
      showMilitaryGlow: true,
      preferLabelCallsign: true,

      appMode: 'map',
      filterTab: 'aircraft',
      selectedIcao: null,
      selectedVesselId: null,
      selectedSatId: null,
      arMode: 'off',
      filtersOpen: false,
      panelOpen: false,
      hudPermissionAsked: false,
      followSat: false,
      focusIcao: null,
      focusPoint: null,
      focusSeq: 0,
      focusVesselId: null,
      focusVesselPoint: null,
      focusVesselSeq: 0,
      focusSatId: null,
      focusSatSeq: 0,

      setFilter: (patch) => set((s) => ({ filter: { ...DEFAULT_FILTER, ...s.filter, ...patch } })),
      resetFilter: () => set({ filter: { ...DEFAULT_FILTER } }),
      setVesselFilter: (patch) =>
        set((s) => ({ vesselFilter: { ...DEFAULT_VESSEL_FILTER, ...s.vesselFilter, ...patch } })),
      resetVesselFilter: () => set({ vesselFilter: { ...DEFAULT_VESSEL_FILTER } }),
      setSatelliteFilter: (patch) =>
        set((s) => ({ satelliteFilter: { ...DEFAULT_SATELLITE_FILTER, ...s.satelliteFilter, ...patch } })),
      resetSatelliteFilter: () => set({ satelliteFilter: { ...DEFAULT_SATELLITE_FILTER } }),

      setAppMode: (mode) =>
        set((s) =>
          mode === 'map'
            ? {
                appMode: mode,
                arMode: 'off',
                selectedSatId: null,
                panelOpen: false,
                followSat: false,
                focusSatId: null,
                focusSatSeq: 0,
                filterTab: s.filterTab === 'satellites' ? 'aircraft' : s.filterTab,
              }
            : {
                appMode: mode,
                selectedIcao: null,
                selectedVesselId: null,
                arMode: 'off',
                followSat: false,
                focusIcao: null,
                focusPoint: null,
                focusVesselId: null,
                focusVesselPoint: null,
                focusSatId: null,
                focusSatSeq: 0,
                filterTab: 'satellites',
              },
        ),

      setFilterTab: (tab) => set({ filterTab: tab }),

      setSelected: (icao) =>
        set({
          selectedIcao: icao,
          selectedVesselId: null,
          selectedSatId: null,
          panelOpen: icao != null,
        }),
      setSelectedVessel: (id) =>
        set({
          selectedVesselId: id,
          selectedIcao: null,
          selectedSatId: null,
          panelOpen: id != null,
        }),
      setSelectedSat: (noradId) =>
        set({
          selectedSatId: noradId,
          selectedIcao: null,
          selectedVesselId: null,
          panelOpen: noradId != null,
        }),

      focusAircraft: (icao, point = null) =>
        set((s) => ({
          selectedIcao: icao,
          selectedVesselId: null,
          selectedSatId: null,
          panelOpen: true,
          focusIcao: icao,
          focusPoint: point,
          focusSeq: s.focusSeq + 1,
        })),
      focusVessel: (id, point = null) =>
        set((s) => ({
          selectedVesselId: id,
          selectedIcao: null,
          selectedSatId: null,
          panelOpen: true,
          focusVesselId: id,
          focusVesselPoint: point,
          focusVesselSeq: s.focusVesselSeq + 1,
        })),
      focusSatellite: (noradId) =>
        set((s) => ({
          selectedSatId: noradId,
          selectedIcao: null,
          selectedVesselId: null,
          panelOpen: true,
          focusSatId: noradId,
          focusSatSeq: s.focusSatSeq + 1,
        })),
      clearFocus: () =>
        set({
          focusIcao: null,
          focusPoint: null,
          focusVesselId: null,
          focusVesselPoint: null,
          focusSatId: null,
        }),

      setArMode: (m) => set({ arMode: m }),
      setFiltersOpen: (v) => set({ filtersOpen: v }),
      setPanelOpen: (v) => set({ panelOpen: v }),
      setFollowSat: (v) => set({ followSat: v }),
      setHudPermissionAsked: (v) => set({ hudPermissionAsked: v }),
      togglePref: (k, v) => set({ [k]: v } as Partial<AppState>),
    }),
    {
      name: 'flightscanner-prefs',
      partialize: (s) => ({
        filter: { ...DEFAULT_FILTER, ...s.filter },
        vesselFilter: { ...DEFAULT_VESSEL_FILTER, ...s.vesselFilter },
        satelliteFilter: { ...DEFAULT_SATELLITE_FILTER, ...s.satelliteFilter },
        showMilitaryGlow: s.showMilitaryGlow,
        preferLabelCallsign: s.preferLabelCallsign,
      }),
      merge: (persisted, current) => {
        const state = persisted as Partial<AppState> | undefined;
        return {
          ...current,
          ...state,
          filter: { ...DEFAULT_FILTER, ...(state?.filter ?? {}) },
          vesselFilter: { ...DEFAULT_VESSEL_FILTER, ...(state?.vesselFilter ?? {}) },
          satelliteFilter: { ...DEFAULT_SATELLITE_FILTER, ...(state?.satelliteFilter ?? {}) },
          appMode: 'map',
          selectedIcao: null,
          selectedVesselId: null,
          selectedSatId: null,
          panelOpen: false,
          followSat: false,
          focusIcao: null,
          focusPoint: null,
          focusSeq: 0,
          focusVesselId: null,
          focusVesselPoint: null,
          focusVesselSeq: 0,
          focusSatId: null,
          focusSatSeq: 0,
        };
      },
    },
  ),
);
